import { createApp } from 'vue';

export default function registerClientAccountGoalDrawer(app) {
    app.component('client-account-goal-drawer', {
        props: { accountId: { type: Number, required: true } },
        data: () => ({ open: false, loading: false, saving: false, error: '', message: '', title: '', url: '', journalId: 0, sequence: 0, contentReady: false, formDirty: false }),
        mounted() {
            this.confirmActionHandler = () => {
                const action = this.pendingConfirmation;
                this.pendingConfirmation = null;
                if (this.open && action?.state === this.formState && typeof action.state[action.method] === 'function') {
                    action.state[action.method](...action.args);
                }
            };
            this.$root.admin2026ConfirmClientGoalDrawerAction = this.confirmActionHandler;
            this.clickHandler = event => {
                if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
                const link = event.target.closest?.('a[href]');
                if (!link || !link.closest('.admin2026-shell') || (link.target && link.target !== '_self')) return;
                const destination = this.goalUrl(link.href);
                if (!destination) return;
                event.preventDefault();
                event.stopPropagation();
                if (link.closest('[data-admin2026-check-ins-tools]')) this.$root.admin2026CheckInsCloseTools?.();
                this.previousFocus = link;
                this.open = true;
                this.message = '';
                this.load(destination);
            };
            document.addEventListener('click', this.clickHandler, true);
        },
        beforeUnmount() {
            document.removeEventListener('click', this.clickHandler, true);
            if (this.$root.admin2026ConfirmClientGoalDrawerAction === this.confirmActionHandler) {
                delete this.$root.admin2026ConfirmClientGoalDrawerAction;
            }
            ++this.sequence;
            this.disposeContent();
        },
        methods: {
            goalUrl(raw) {
                try {
                    const url = new URL(raw, window.location.href);
                    const match = url.pathname.match(new RegExp('^/pono-admin/workspaces/clients/accounts/' + this.accountId + '/check-ins/journals/(\\d+)/goals/(edit|create|history)$'));
                    if (url.origin !== window.location.origin || !match) return null;
                    url.searchParams.set('goal_drawer', '1');
                    return { url: url.pathname + url.search, journalId: Number(match[1]), mode: match[2] };
                } catch (_) { return null; }
            },
            disposeContent() {
                this.pendingConfirmation = null;
                this.contentApp?.unmount();
                this.contentApp = null;
                this.formState = null;
                this.contentReady = false;
                this.formDirty = false;
            },
            async load(destination) {
                if (!destination || this.saving) return;
                const sequence = ++this.sequence;
                this.url = destination.url;
                this.journalId = destination.journalId;
                this.title = { edit: 'Edit goals', create: 'Add goal', history: 'Goal history' }[destination.mode];
                this.loading = true;
                this.error = '';
                this.disposeContent();
                try {
                    const response = await this.$root.axios.get(this.url, { headers: { Accept: 'application/json' } });
                    if (sequence !== this.sequence || !this.open) return;
                    if (response.data?.success !== true || !response.data.content) throw new Error('Invalid goal response');
                    await this.$nextTick();
                    const owner = this;
                    // This is the existing server-rendered native goal-form mixin, from the authenticated goal route.
                    const mixin = new Function('return (' + response.data.mixin + ')')();
                    const contentApp = createApp({
                        template: response.data.content,
                        mixins: [{ data: app._component.data, methods: app._component.methods }, mixin],
                        methods: {
                            openModalOk: (...args) => owner.$root.openModalOk(...args),
                            openModalWarning: (...args) => owner.$root.openModalWarning(...args),
                            openModalConfirm(title, content, primary, cancel, method, id1 = null, id2 = null, id3 = null) {
                                owner.pendingConfirmation = { state: this, method, args: [id1, id2, id3].filter(value => value !== null) };
                                owner.$root.openModalConfirm(title, content, primary, cancel, 'admin2026ConfirmClientGoalDrawerAction');
                            },
                            touchForm() { this.formTouched = true; owner.formDirty = true; },
                            admin2026SetDirtyFormState() {},
                            submitForm() { owner.save(this); },
                            submitJournalForm() {
                                if (!String(this.goal_title || '').trim()) {
                                    owner.error = 'Please enter a title for this goal before saving it.';
                                    return;
                                }
                                owner.save(this);
                            },
                        },
                    });
                    Object.entries(app._context.components).forEach(([name, component]) => contentApp.component(name, component));
                    for (const [, name] of response.data.content.matchAll(/<([A-Z][A-Za-z0-9]*)\b/g)) {
                        const component = app._context.components[name.toLowerCase()];
                        if (component) contentApp.component(name, component);
                    }
                    contentApp.config.globalProperties.axios = this.$root.axios;
                    this.contentApp = contentApp;
                    this.formState = contentApp.mount(this.$refs.content);
                    this.contentReady = true;
                    this.$refs.panel?.focus({ preventScroll: true });
                } catch (_) {
                    if (sequence === this.sequence) this.error = 'Goals could not be loaded. Please try again.';
                } finally {
                    if (sequence === this.sequence) this.loading = false;
                }
            },
            async save(formState) {
                if (this.saving) return;
                const form = formState.$refs.form;
                if (!form) return;
                this.saving = true;
                this.error = '';
                this.message = '';
                try {
                    if (typeof formState.submitFormAdditional === 'function') formState.submitFormAdditional();
                    await formState.$nextTick();
                    const body = new FormData(form);
                    body.set('goal_drawer', '1');
                    const response = await this.$root.axios.post(form.action, body, { headers: { Accept: 'application/json' } });
                    if (response.data?.success !== true) throw new Error('Goal save failed');
                    this.message = 'Goals saved.';
                    // Refresh only the journal fragment, without touching host navigation, filters or scroll.
                    const host = this.$root;
                    const journalId = this.journalId;
                    if (typeof host.admin2026CheckInsFetchJournalFragment === 'function') {
                        const query = typeof host.admin2026CheckInsBuildQuery === 'function' ? host.admin2026CheckInsBuildQuery() : {};
                        host.admin2026CheckInsFetchJournalFragment(journalId, query).then(payload => {
                            if (!payload) return;
                            const section = host.admin2026CheckInsJournalSection(journalId);
                            const goals = section?.querySelector('[data-admin2026-check-ins-goals-shell="' + journalId + '"]');
                            if (goals) goals.innerHTML = payload.goals_html || '';
                            // Update goal cells only; leave check-in inputs, charts and photo comparisons mounted.
                            const updated = new DOMParser().parseFromString(payload.content_html || '', 'text/html');
                            const selector = '[data-admin2026-check-ins-table-goal-item="' + journalId + '"]';
                            const cells = Array.from(updated.querySelectorAll(selector));
                            section?.querySelectorAll(selector).forEach((cell, index) => {
                                if (cells[index]) cell.replaceWith(document.importNode(cells[index], true));
                            });
                            host.admin2026CheckInsSyncGoalRows(journalId, section);
                        }).catch(() => { if (this.open && this.journalId === journalId) this.message = 'Goals saved. Reopen check-ins to refresh the displayed goals.'; });
                    }
                    this.saving = false;
                    await this.load(this.goalUrl(this.url.replace(/\/goals\/(create|edit)(?=\?|$)/, '/goals/edit')));
                } catch (error) {
                    const payload = error.response?.status === 422 ? (error.response.data || {}) : {};
                    const messages = Object.values(payload.errors || {}).flat();
                    this.error = messages.length ? messages.join(' ') : (payload.message || 'Goals could not be saved. Please check the fields and try again.');
                } finally { this.saving = false; }
            },
            close() {
                if (this.saving) return;
                ++this.sequence;
                this.disposeContent();
                this.open = false;
                const previous = this.previousFocus;
                const trigger = previous?.isConnected ? previous : Array.from(document.querySelectorAll('a[href]')).find(link => link.href === previous?.href);
                trigger?.focus({ preventScroll: true });
            },
            contentSubmit(event) {
                event.preventDefault();
                if (!this.formState) return;
                if (this.goalUrl(this.url)?.mode === 'create') this.formState.submitJournalForm();
                else this.formState.submitForm();
            },
            contentClick(event) {
                const link = event.target.closest?.('a[href]');
                if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
                if (link.pathname === '/pono-admin/workspaces/clients/accounts/' + this.accountId + '/check-ins') {
                    event.preventDefault();
                    event.stopPropagation();
                    this.close();
                }
            },
        },
        template: `
            <div v-if="open" class="fixed inset-0 z-50 overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="client-goal-drawer-title" data-admin2026-client-goal-drawer @keydown.esc.stop.prevent="close">
                <div class="absolute inset-0 admin2026-drawer-backdrop cursor-pointer" @click="close" @wheel.prevent></div>
                <div class="pointer-events-none absolute inset-y-0 right-0 flex max-w-full admin2026-drawer-rail">
                    <div ref="panel" tabindex="-1" class="pointer-events-auto relative flex h-full w-screen flex-col admin2026-drawer-panel bg-white dark:bg-slate-900" @click.stop>
                        <div class="flex shrink-0 items-center justify-between gap-4 border-b border-slate-200 px-5 py-5 dark:border-slate-800 sm:px-6">
                            <h2 id="client-goal-drawer-title" class="text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">{{ title }}</h2>
                            <div class="flex shrink-0 items-center gap-2">
                                <div id="client-goal-drawer-header-actions"></div>
                                <button type="button" class="inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-800" aria-label="Close goals" :disabled="saving" @click="close"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
                            </div>
                        </div>
                        <div class="relative flex-1 overflow-y-auto overscroll-contain" @click.capture="contentClick" @submit.capture="contentSubmit">
                            <p v-if="error" role="alert" class="m-4 text-rose-700 dark:text-rose-200">{{ error }}</p>
                            <p v-if="message" role="status" class="m-4 text-emerald-700 dark:text-emerald-200">{{ message }}</p>
                            <button v-if="error && !contentReady" class="m-4 cursor-pointer tbtn tbtn-outline-primary" @click="load(goalUrl(url))">Try again</button>
                            <div ref="content"></div>
                            <div v-if="loading || saving" class="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-slate-950/75" role="status"><span>{{ saving ? 'Saving goals…' : 'Loading goals…' }}</span></div>
                        </div>
                        <div v-show="contentReady && formDirty && ['create', 'edit'].includes(goalUrl(url)?.mode)" class="shrink-0 border-t border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-900 sm:px-6"><div id="client-goal-drawer-footer-actions" class="flex justify-end"></div></div>
                    </div>
                </div>
            </div>`,
    });
}
